import type { RoomFieldDto, RoomTemplateDto } from '@reos/field-intake';

/**
 * Krok NAB3 — MISTNOSTI NABORU v prohlizeci.
 *
 * Sablona je data ze serveru (`GET /v1/field-intake/room-template`), ale
 * predgenerovani z dispozice, „stejne jako predchozi mistnost", hromadne
 * predvolby a souhrnna veta musi umet i prohlizec: makler nabira BEZ SIGNALU
 * a hodnoty lezi v offline fronte, takze je server pri pocitani nevidi.
 *
 * Kontext `@reos/field-intake` smi web-admin importovat JEN jako typ
 * (`test/browser-safe-imports.spec.ts`), proto je to lokalni kopie pravidel —
 * stejny vzor jako `intake-checklist.ts` (NAB2). Ze se s kontextem nerozesla,
 * hlida test parity `intake-rooms.spec.ts`: vola OBE implementace nad
 * skutecnou sablonou.
 */

export type RoomValue = string | number | boolean | null | readonly string[];
export type Room = Readonly<Record<string, RoomValue>>;

/** Klic faktu, pod kterym seznam mistnosti zije (registr `FACT_KEYS`). */
export const ROOM_LIST_FACT_KEY = 'room_list';

export function roomFieldOf(template: RoomTemplateDto, id: string): RoomFieldDto | undefined {
  return template.fields.find((field) => field.id === id);
}

export function roomTypeDefOf(
  template: RoomTemplateDto,
  value: unknown,
): RoomTemplateDto['roomTypes'][number] | undefined {
  return typeof value !== 'string' ? undefined : template.roomTypes.find((type) => type.value === value);
}

/** `room_type` fotky pro portal (`MEDIA_ROOM_TYPES`); `null` = neposila se. */
export function mediaRoomTypeOf(template: RoomTemplateDto, room: Room): string | null {
  return roomTypeDefOf(template, room['room_type'])?.mediaRoomType ?? null;
}

function textOf(room: Room, key: string): string | null {
  const value = room[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/** „Obývací pokoj 22 m², plovoucí podlaha, plastová okna trojsklo 2015, …" */
export function roomSummary(template: RoomTemplateDto, room: Room): string {
  const head = textOf(room, 'name') ?? roomTypeDefOf(template, room['room_type'])?.name ?? '';
  let text = head;
  for (const fieldId of template.summaryOrder) {
    const field = roomFieldOf(template, fieldId);
    const value = room[fieldId];
    if (field === undefined || value === null || value === undefined || value === '') continue;
    const option = field.options.find((candidate) => candidate.value === value);
    const unit = field.unit === null ? '' : ` ${field.unit}`;
    const part = option?.label ?? `${String(value)}${unit}`;
    if (text === '') text = part;
    else text += field.summaryGlue === 'space' ? ` ${part}` : `, ${part}`;
  }
  const shading = template.fields
    .filter((field) => field.copyGroup === 'shading' && field.control === 'yes_no_unknown' && room[field.id] === true)
    .map((field) => field.question.toLocaleLowerCase('cs'));
  if (shading.length > 0) text += `, ${shading.join(', ')}`;
  return text.slice(0, 200);
}

/** Hodnota faktu `room_list` — veta se vzdy pocita znovu (jedna cesta zapisu). */
export function roomListFactValue(template: RoomTemplateDto, rooms: readonly Room[]): readonly Room[] {
  return rooms.map((room) => ({ ...room, summary: roomSummary(template, room) }));
}

function nextRoomId(type: string, taken: ReadonlySet<string>): string {
  for (let index = 1; index < 100; index += 1) {
    const candidate = `${type}_${String(index)}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${type}_${String(taken.size + 1)}`;
}

export function newRoom(template: RoomTemplateDto, type: string, taken: readonly Room[] = []): Room {
  const def = roomTypeDefOf(template, type);
  const ids = new Set(taken.map((room) => String(room['id'] ?? '')));
  const sameType = taken.filter((room) => room['room_type'] === type).length;
  const name = def === undefined ? type : sameType === 0 ? def.name : `${def.name} ${String(sameType + 1)}`;
  return { id: nextRoomId(type, ids), room_type: type, name };
}

/** Predgenerovani z dispozice; neznama dispozice = prazdny seznam. */
export function roomsForDisposition(template: RoomTemplateDto, disposition: unknown): readonly Room[] {
  const plan = template.dispositions.find((entry) => entry.value === disposition);
  if (plan === undefined) return [];
  const rooms: Room[] = [];
  for (const type of plan.rooms) rooms.push(newRoom(template, type, rooms));
  return rooms;
}

export interface RoomPlanDiff {
  readonly add: readonly Room[];
  readonly extra: readonly Room[];
  readonly matches: boolean;
}

/** Zmenena dispozice: co PRIDAT a co je nad rozpad. NIC nemaze. */
export function planRoomsForDisposition(
  template: RoomTemplateDto,
  current: readonly Room[],
  disposition: unknown,
): RoomPlanDiff {
  const plan = template.dispositions.find((entry) => entry.value === disposition);
  if (plan === undefined) return { add: [], extra: [], matches: true };
  const remaining = [...current];
  const add: Room[] = [];
  for (const type of plan.rooms) {
    const hit = remaining.findIndex((room) => room['room_type'] === type);
    if (hit >= 0) remaining.splice(hit, 1);
    else add.push(newRoom(template, type, [...current, ...add]));
  }
  return { add, extra: remaining, matches: add.length === 0 && remaining.length === 0 };
}

export function addRoom(template: RoomTemplateDto, rooms: readonly Room[], type: string): readonly Room[] {
  return [...rooms, newRoom(template, type, rooms)];
}

export function removeRoom(rooms: readonly Room[], roomId: string): readonly Room[] {
  return rooms.filter((room) => room['id'] !== roomId);
}

export function renameRoom(rooms: readonly Room[], roomId: string, name: string): readonly Room[] {
  const text = name.trim().slice(0, 120);
  return rooms.map((room) =>
    room['id'] === roomId ? { ...room, name: text === '' ? room['name'] ?? '' : text } : room,
  );
}

export function setRoomField(
  template: RoomTemplateDto,
  rooms: readonly Room[],
  roomId: string,
  fieldId: string,
  value: RoomValue,
): readonly Room[] {
  if (roomFieldOf(template, fieldId) === undefined) return rooms;
  return rooms.map((room) => (room['id'] === roomId ? { ...room, [fieldId]: value } : room));
}

/** „Stejne jako predchozi mistnost" pro podlahu, okna, stineni. */
export function copyFromPreviousRoom(
  template: RoomTemplateDto,
  rooms: readonly Room[],
  roomId: string,
  group: 'floor' | 'windows' | 'shading',
): readonly Room[] {
  const index = rooms.findIndex((room) => room['id'] === roomId);
  const previous = index > 0 ? rooms[index - 1] : undefined;
  const target = index >= 0 ? rooms[index] : undefined;
  if (previous === undefined || target === undefined) return rooms;
  const patch: Record<string, RoomValue> = {};
  for (const field of template.fields) {
    if (field.copyGroup !== group) continue;
    const value = previous[field.id];
    if (value !== undefined) patch[field.id] = value;
  }
  return rooms.map((room, at) => (at === index ? { ...room, ...patch } : room));
}

/** Hromadna predvolba („vsechna okna plast trojsklo, 2015"). */
export function applyBulkPreset(
  template: RoomTemplateDto,
  rooms: readonly Room[],
  presetId: string,
): readonly Room[] {
  const preset = template.bulkPresets.find((candidate) => candidate.id === presetId);
  if (preset === undefined) return rooms;
  const patch: Record<string, RoomValue> = {};
  for (const [fieldId, value] of Object.entries(preset.values)) {
    if (roomFieldOf(template, fieldId) !== undefined) patch[fieldId] = value;
  }
  const touchesWindows = Object.keys(patch).some(
    (fieldId) => roomFieldOf(template, fieldId)?.copyGroup === 'windows',
  );
  return rooms.map((room) => (touchesWindows && room['windows_count'] === 0 ? room : { ...room, ...patch }));
}

/** Pole mistnosti po skupinach sablony (poradi obrazovky). */
export function roomFieldsByGroup(
  template: RoomTemplateDto,
): readonly { readonly id: string; readonly title: string; readonly fields: readonly RoomFieldDto[] }[] {
  return template.groups.map((group) => ({
    id: group.id,
    title: group.title,
    fields: template.fields.filter((field) => field.group === group.id),
  }));
}

/** Kolik polí mistnosti je vyplneno (hlavicka karty „vyplneno/celkem"). */
export function roomDone(template: RoomTemplateDto, room: Room): number {
  return template.fields.filter((field) => {
    const value = room[field.id];
    return value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);
  }).length;
}
